import { useState } from 'react';
import { Link } from 'react-router-dom';
import { stories } from '@/mocks/stories';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';

const tracked = stories.filter((item) => item.status === '追踪中').slice(0, 3);

const levelTone: Record<string, string> = {
  已确认: 'bg-secondary-100 text-secondary-900',
  多方印证: 'bg-accent-100 text-accent-900',
  存疑: 'bg-primary-100 text-primary-800',
  暂无法确认: 'bg-background-200 text-foreground-700',
};

export default function TrackSection() {
  const [activeId, setActiveId] = useState(tracked[0]?.id ?? '');
  const active = tracked.find((item) => item.id === activeId) ?? tracked[0];

  if (!active) return null;

  return (
    <section id="track" className="bg-background-950 py-16 text-background-100 md:py-20">
      <div className="mx-auto w-full px-4 md:px-6">
        <div className="max-w-2xl">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-accent-400">
            <span className="h-px w-6 bg-accent-500" />
            线索追踪台
          </p>
          <h2 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-3xl">
            一条瓜是怎么被追出来的
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-background-300 md:text-base">
            选一条正在追踪的线索，看它的时间线、涉及人物与当前判定。这些结论会随着新证据调整。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {tracked.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveId(item.id)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                item.id === active.id
                  ? 'bg-accent-500 font-medium text-accent-950'
                  : 'bg-background-800 text-background-200 hover:bg-background-700'
              }`}
            >
              {item.category}
            </button>
          ))}
        </div>

        <Reveal className="mt-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h3 className="font-heading text-xl font-bold text-background-50">{active.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-background-300">{active.excerpt}</p>

              <ol className="mt-8 space-y-6">
                {active.timeline.map((node, index) => (
                  <li key={node.time} className="relative flex gap-4 pl-1">
                    <span className="flex flex-col items-center">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                          index === 0 ? 'bg-primary-500 text-background-50' : 'bg-background-700 text-background-100'
                        }`}
                      >
                        {index + 1}
                      </span>
                      {index < active.timeline.length - 1 && (
                        <span className="mt-1 w-px flex-1 bg-background-700" />
                      )}
                    </span>
                    <span className="pb-2">
                      <span className="block text-xs font-medium tracking-wide text-accent-400">
                        {node.time}
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-background-200">
                        {node.text}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>

              <div className="mt-8 rounded-lg border border-background-700 bg-background-900 p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-background-400">当前判定</span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${levelTone[active.verdictLevel]}`}
                  >
                    {active.verdictLevel}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-background-200">{active.verdict}</p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-700 bg-background-900 p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                  <i className="ri-user-search-line text-accent-400" />
                  涉及人物与账号
                </h3>
                <ul className="mt-5 space-y-5">
                  {active.personas.map((persona) => (
                    <li key={persona.name} className="flex gap-4">
                      <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-background-700">
                        <img
                          src={persona.avatar}
                          alt={`${persona.name} 示意图`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-background-50">{persona.name}</span>
                          <span className="rounded-full bg-primary-500/90 px-2 py-0.5 text-xs text-background-50">
                            {persona.tag}
                          </span>
                        </span>
                        <span className="mt-1 block text-xs text-background-400">{persona.role}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-background-300">
                          {persona.note}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/melon/${active.id}`}
                  className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看完整档案
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}